import { lazy } from "react";
import { Navigate } from "react-router-dom";
import Loadable from "app/components/Loadable";
import OperationalRouteGuard from "app/components/operational-workspace/OperationalRouteGuard";
import RadiologyShellLayout from "./RadiologyShellLayout";
import { RadConfigConsoleLayout } from "lab-catalog-shell/modules/radiology/config/RadConfigConsoleLayout";

const RadiologyCounterPage = Loadable(
  lazy(() =>
    import("lab-catalog-shell/modules/radiology/pages/RadiologyRegistrationCounterPage").then((m) => ({
      default: m.RadiologyRegistrationCounterPage,
    })),
  ),
);

const RadiologySchedulingQueuePage = Loadable(
  lazy(() =>
    import("lab-catalog-shell/modules/radiology/pages/RadiologySchedulingQueuePage").then((m) => ({
      default: m.RadiologySchedulingQueuePage,
    })),
  ),
);

const RadiologyTechnicianQueuePage = Loadable(
  lazy(() =>
    import("lab-catalog-shell/modules/radiology/pages/RadiologyTechnicianQueuePage").then((m) => ({
      default: m.RadiologyTechnicianQueuePage,
    })),
  ),
);

const RadiologyReportEntryPage = Loadable(
  lazy(() =>
    import("lab-catalog-shell/modules/radiology/pages/RadiologyReportEntryPage").then((m) => ({
      default: m.RadiologyReportEntryPage,
    })),
  ),
);

const RadiologyReportWorklistPage = Loadable(
  lazy(() =>
    import("lab-catalog-shell/modules/radiology/pages/RadiologyReportWorklistPage").then((m) => ({
      default: m.RadiologyReportWorklistPage,
    })),
  ),
);

const RadiologyStructuredReportSearchPage = Loadable(
  lazy(() =>
    import("lab-catalog-shell/modules/radiology/pages/RadiologyStructuredReportSearchPage").then(
      (m) => ({
        default: m.RadiologyStructuredReportSearchPage,
      }),
    ),
  ),
);

const RadiologyVerificationQueuePage = Loadable(
  lazy(() =>
    import("lab-catalog-shell/modules/radiology/pages/RadiologyVerificationQueuePage").then((m) => ({
      default: m.RadiologyVerificationQueuePage,
    })),
  ),
);

const RadiologyReportPrintQueuePage = Loadable(
  lazy(() =>
    import("lab-catalog-shell/modules/radiology/pages/RadiologyReportPrintQueuePage").then((m) => ({
      default: m.RadiologyReportPrintQueuePage,
    })),
  ),
);

const RadConfigOverviewPage = Loadable(
  lazy(() =>
    import("lab-catalog-shell/modules/radiology/config/RadConfigOverviewPage").then((m) => ({
      default: m.RadConfigOverviewPage,
    })),
  ),
);

const RadEnterpriseMasterPage = Loadable(
  lazy(() =>
    import("lab-catalog-shell/modules/radiology/pages/RadEnterpriseMasterPage").then((m) => ({
      default: m.RadEnterpriseMasterPage,
    })),
  ),
);

const RadHospitalMappingPage = Loadable(
  lazy(() =>
    import("lab-catalog-shell/modules/radiology/pages/RadHospitalMappingPage").then((m) => ({
      default: m.RadHospitalMappingPage,
    })),
  ),
);

const RadReportTemplateListPage = Loadable(
  lazy(() =>
    import("lab-catalog-shell/modules/radiology/pages/RadReportTemplateListPage").then((m) => ({
      default: m.RadReportTemplateListPage,
    })),
  ),
);

const RadReportTemplateDetailPage = Loadable(
  lazy(() =>
    import("lab-catalog-shell/modules/radiology/pages/RadReportTemplateDetailPage").then((m) => ({
      default: m.RadReportTemplateDetailPage,
    })),
  ),
);

const RadReportOptionCatalogAdminPage = Loadable(
  lazy(() =>
    import("lab-catalog-shell/modules/radiology/pages/RadReportOptionCatalogAdminPage").then(
      (m) => ({
        default: m.RadReportOptionCatalogAdminPage,
      }),
    ),
  ),
);

const RadFormulaRegistryAdminPage = Loadable(
  lazy(() =>
    import("lab-catalog-shell/modules/radiology/pages/RadFormulaRegistryAdminPage").then((m) => ({
      default: m.RadFormulaRegistryAdminPage,
    })),
  ),
);

const RadDicomGatewayAdminPage = Loadable(
  lazy(() =>
    import("lab-catalog-shell/modules/radiology/config/RadDicomGatewayAdminPage").then((m) => ({
      default: m.RadDicomGatewayAdminPage,
    })),
  ),
);

function guarded(element) {
  return (
    <OperationalRouteGuard moduleId="RADIOLOGY_COUNTER">
      <RadiologyShellLayout>{element}</RadiologyShellLayout>
    </OperationalRouteGuard>
  );
}

/**
 * Native radiology module — lab-catalog-shell port of frontend AppRoutes radiology paths.
 * Replaces thin rad execution stubs for overlapping paths.
 */
const RadiologyModuleRoutes = [
  {
    path: "/radiology/counter",
    element: guarded(<RadiologyCounterPage />),
  },
  {
    path: "/radiology/scheduling",
    element: guarded(<RadiologySchedulingQueuePage />),
  },
  {
    path: "/radiology/technician",
    element: guarded(<RadiologyTechnicianQueuePage />),
  },
  {
    path: "/radiology/report-entry",
    element: guarded(<RadiologyReportEntryPage />),
  },
  {
    path: "/radiology/reporting",
    element: guarded(<RadiologyReportWorklistPage />),
  },
  {
    path: "/radiology/worklist",
    element: guarded(<RadiologyReportWorklistPage />),
  },
  {
    path: "/radiology/structured-search",
    element: guarded(<RadiologyStructuredReportSearchPage />),
  },
  {
    path: "/radiology/verification",
    element: guarded(<RadiologyVerificationQueuePage />),
  },
  {
    path: "/radiology/print-queue",
    element: guarded(<RadiologyReportPrintQueuePage />),
  },
  {
    path: "/radiology/scan-execution",
    element: <Navigate to="/radiology/technician" replace />,
  },
  {
    path: "/radiology/admin/config",
    element: guarded(<RadConfigConsoleLayout />),
    children: [
      { index: true, element: <RadConfigOverviewPage /> },
      { path: "enterprise", element: <RadEnterpriseMasterPage /> },
      { path: "hospital-pricing", element: <RadHospitalMappingPage /> },
      { path: "templates", element: <RadReportTemplateListPage /> },
      { path: "templates/:templateId", element: <RadReportTemplateDetailPage /> },
      { path: "option-catalogs", element: <RadReportOptionCatalogAdminPage /> },
      { path: "formula-registry", element: <RadFormulaRegistryAdminPage /> },
      { path: "dicom-gateway", element: <RadDicomGatewayAdminPage /> },
    ],
  },
  {
    path: "/radiology/admin/enterprise",
    element: <Navigate to="/radiology/admin/config/enterprise" replace />,
  },
  {
    path: "/radiology/admin/hospital-mappings",
    element: <Navigate to="/radiology/admin/config/hospital-pricing" replace />,
  },
  {
    path: "/radiology/admin/templates",
    element: <Navigate to="/radiology/admin/config/templates" replace />,
  },
  {
    path: "/radiology/admin/templates/:templateId",
    element: guarded(<RadReportTemplateDetailPage />),
  },
  {
    path: "/radiology/admin/option-catalogs",
    element: <Navigate to="/radiology/admin/config/option-catalogs" replace />,
  },
  {
    path: "/radiology/admin/formula-registry",
    element: <Navigate to="/radiology/admin/config/formula-registry" replace />,
  },
];

export default RadiologyModuleRoutes;
